import type { TelemetryResolution } from '@/features/telemetry/services/schemas';

// ─── Range presets ────────────────────────────────────────────────────────────

export const RANGE_PRESETS = {
  '6h': { minutes: 6 * 60 },
  '24h': { minutes: 24 * 60 },
  '7d': { minutes: 7 * 24 * 60 },
} as const;
export type RangePreset = keyof typeof RANGE_PRESETS;
export const DEFAULT_RANGE_PRESET: RangePreset = '24h';

/** Design cap. The API itself allows a window of up to 31 days. */
export const MAX_RANGE_MINUTES = RANGE_PRESETS['7d'].minutes;

// ─── Resolution ───────────────────────────────────────────────────────────────

/**
 * Windows at or below this span request 1-minute buckets; longer windows request
 * 15-minute buckets.
 *
 * Deliberately narrower than the API's own `resolution=auto` rule (1-minute for any
 * window <= 24h). At the 24h preset, `auto` would mean 1,440 points per device; capping
 * the fine-resolution window at 6h instead keeps the default 24h view at 96 points per
 * device while 6h stays maximally detailed. We always send an explicit `resolution` —
 * see TelemetryService.getTelemetry — so this boundary, not the server's, decides.
 */
export const FINE_RESOLUTION_MAX_MINUTES = 6 * 60;

export const RESOLUTION_BUCKET_MS: Record<TelemetryResolution, number> = {
  '1min': 60_000,
  '15min': 15 * 60_000,
};

// ─── Chart appearance ───────────────────────────────────────────────────────────

/** Numeric series colours, in assignment order. Tokens already exist in globals.css. */
export const CHART_SERIES_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
] as const;

/**
 * Status segment colours. Status values are opaque backend strings with no semantic
 * meaning attached — a colour is a hash of the raw value, never a lookup by known
 * status name. Deliberately not reusing CHART_SERIES_COLORS, so a device's numeric
 * series and its status segments never fight for the same hues in one card.
 */
export const STATUS_SEGMENT_COLORS = [
  'var(--color-blue-500)',
  'var(--color-emerald-500)',
  'var(--color-amber-500)',
  'var(--color-violet-500)',
  'var(--color-red-500)',
  'var(--color-cyan-500)',
  'var(--color-pink-500)',
  'var(--color-lime-500)',
  'var(--color-orange-500)',
  'var(--color-teal-500)',
  'var(--color-purple-500)',
  'var(--color-slate-500)',
] as const;

export const CHART_HEIGHT_PX = 180;

/**
 * Units that claim the primary (left) axis when a device has one, since power is
 * almost always the metric the chart is really about — a battery's kW curve should
 * anchor the main axis with its SOC% pushed to the secondary one, not the reverse.
 * Lowercase; matched case-insensitively against the already-display-mapped unit
 * (see utils/unitDisplay.ts), so both 'W' and 'kW' match regardless of source casing.
 */
export const MAIN_AXIS_UNITS = ['w', 'kw'] as const;

// ─── EMS status thresholds ──────────────────────────────────────────────────────

export const EMS_WARNING_AFTER_MS = 2 * 60_000;
export const EMS_ERROR_AFTER_MS = 10 * 60_000;
